import { type SerializedStyles } from '@emotion/react';
import tw, { css, styled } from 'twin.macro';
import Icon from '@/components/Icon/IconAsset';
import isPropValid from '@emotion/is-prop-valid';

const blockStyles = css`
  ${tw`transition-colors duration-150 ease-in`}
  ${tw`inline-flex justify-center items-center gap-[4px]`}
  ${tw`whitespace-nowrap`}
  ${tw`rounded-lg`}
  ${tw`font-inter font-semi-bold`}
  ${tw`text-text-cta`}
  ${tw`border-2 border-solid border-transparent`}
  ${tw`disabled:hover:cursor-not-allowed`}
`;

export const primaryStyles = css`
  ${blockStyles}
  ${tw`relative`}
  ${tw`text-neutrals-n1`}
  ${tw`overflow-hidden`}
  ${tw`z-0`}
  // The 'border-box' keyword at the end ensures the gradient background covers the border area, not just the content or padding.
  background: linear-gradient(to bottom, #126ed3, #0f63bf) border-box;

  ${tw`
    before:content-['']
    before:absolute
    before:inset-0
    before:bg-transparent
    before:z-[-1]
    before:transition-colors
    before:duration-150
    before:ease-in-out
  `}
  ${tw`hover:border-ui-colours-ui2`}
  ${tw`hover:before:bg-ui-colours-ui2`}
  ${tw`disabled:before:bg-ui-colours-ui3 disabled:text-neutrals-n1 disabled:border-ui-colours-ui3`}
`;

export const primaryDarkStyles = css`
  ${blockStyles}
  ${tw`text-neutrals-n1`}
  ${tw`bg-primary-p1 hover:bg-ui-colours-ui5 disabled:bg-ui-colours-ui6 `}
  ${tw`hover:border-ui-colours-ui5 disabled:hover:border-ui-colours-ui6`}
`;

export const secondaryStyles = css`
  ${blockStyles}
  ${tw`border-secondary-s1 hover:border-ui-colours-ui2`}
  ${tw`relative`}
  ${tw`text-primary-p1`}
  ${tw`bg-neutrals-n1 hover:bg-ui-colours-ui1`}
  ${tw`disabled:text-ui-colours-ui3 disabled:border-ui-colours-ui3 disabled:hover:bg-neutrals-n1`}
`;

export const secondaryDarkStyles = css`
  ${blockStyles}
  ${secondaryStyles}
  ${tw`border-primary-p1 hover:border-ui-colours-ui5 disabled:border-ui-colours-ui6`}
  ${tw`disabled:text-ui-colours-ui6 disabled:hover:bg-neutrals-n1`}
`;

const tertiaryStyles = css`
  ${tw`relative`}
  ${tw`inline-flex items-start gap-[4px]`}
  ${tw`font-inter font-semi-bold`}
  ${tw`text-text-cta text-center`}
  ${tw`text-secondary-s1 hover:text-ui-colours-ui2`}
  ${tw`disabled:text-ui-colours-ui3 disabled:hover:cursor-not-allowed`}
`;

export const sizeDefaultStyles = css`
  ${tw`py-[13px] px-[20px]`}
`;

export const sizeSmallStyles = css`
  ${tw`py-[9px] px-[20px]`}
`;

export const sizeTinyStyles = css`
  ${tw`text-tag-s`}
  ${tw`py-[8px] px-[14px]`}
`;

const animatedUnderlineStyles = css`
  ${tw`hover:after:scale-x-100 hover:after:bg-secondary-s1 active:after:scale-x-100 disabled:hover:after:scale-x-0`}

  &:after {
    ${tw`transition-all duration-150`}
    ${tw`absolute`}
    ${tw`origin-left scale-x-0`}
    ${tw`left-0 bottom-0`}
    ${tw`[content: '']`}
    ${tw`w-full h-[4px]`}
    ${tw`bg-ui-colours-ui2`}
  }
  ${tw`font-inter font-semi-bold`}
  ${tw`text-tag-l text-center`}
  ${tw`tracking-[2.4px]`}
  ${tw`text-secondary-s1`}
`;

// NOTE aria-selected is used to determine the active state of the tab
const tabPrimaryStyles = css`
  ${blockStyles}
  ${tw`border-2`}
  ${tw`duration-200`}
  ${tw`focus:outline-none`}

  // NOTE using aria-selected because the current implementation makes it difficult to drill down props (isActive)
  &[aria-selected='false'] {
    ${tw`bg-ui-colours-ui3`}
    ${tw`border-ui-colours-ui3 `}
    ${tw`text-neutrals-n1 hover:text-ui-colours-ui2`}
  }

  &[aria-selected='true'] {
    ${tw`text-neutrals-n1`}
    ${tw`bg-secondary-s1 hover:bg-ui-colours-ui2`}
  }
`;

// NOTE aria-selected is used to determine the active state of the tab
const toggleStyles = css`
  ${blockStyles}
  ${tw`rounded-[52px]`}
  ${tw`text-center text-mobile-text-cta`}
  ${tw`font-inter font-semi-bold`}

  // NOTE using aria-selected because the current implementation makes it difficult to drill down props (isActive)
  &[aria-selected='false'] {
    ${tw`bg-neutrals-n1 hover:bg-ui-colours-ui3`}
    ${tw`text-secondary-s1 hover:text-ui-colours-ui2`}
  }

  &[aria-selected='true'] {
    ${tw`text-neutrals-n1`}
    ${tw`bg-secondary-s1 hover:bg-ui-colours-ui2`}
  }
`;

const plateformLoginStyles = css`
  ${blockStyles}
  ${tw`w-full`}
  ${tw`flex-row justify-start`}
  ${tw`border-ui-colours-ui3 active:border-secondary-s1 hover:border-ui-colours-ui2`}
  ${tw`relative`}
  ${tw`text-primary-p1`}
  ${tw`bg-neutrals-n1 hover:bg-ui-colours-ui1`}
`;

export type LinkVariant =
  | 'nav_text_link'
  | 'primary'
  | 'primary_small'
  | 'primary_tiny'
  | 'primary_dark'
  | 'primary_dark_small'
  | 'primary_dark_tiny'
  | 'secondary'
  | 'secondary_small'
  | 'secondary_tiny'
  | 'secondary_dark'
  | 'secondary_dark_small'
  | 'secondary_dark_tiny'
  | 'tertiary'
  | 'tertiary_no_underline'
  | 'tertiary_dark'
  | 'tertiary_dark_no_underline'
  | 'tab_primary'
  | 'tab_primary_small'
  | 'toggle'
  | 'toggle_small'
  | 'platform_login'
  | 'platform_login_small';

export const variants: Record<LinkVariant, SerializedStyles> = {
  primary: css`
    ${primaryStyles}
    ${sizeDefaultStyles}
  `,
  primary_small: css`
    ${primaryStyles}
    ${sizeSmallStyles}
  `,
  primary_tiny: css`
    ${primaryStyles}
    ${sizeTinyStyles}
  `,
  primary_dark: css`
    ${primaryDarkStyles}
    ${sizeDefaultStyles}
  `,
  primary_dark_small: css`
    ${primaryDarkStyles}
    ${sizeSmallStyles}
  `,
  primary_dark_tiny: css`
    ${primaryDarkStyles}
    ${sizeTinyStyles}
  `,
  secondary: css`
    ${secondaryStyles}
    ${sizeDefaultStyles}
  `,
  secondary_small: css`
    ${secondaryStyles}
    ${sizeSmallStyles}
  `,
  secondary_tiny: css`
    ${secondaryStyles}
    ${sizeTinyStyles}
  `,
  secondary_dark: css`
    ${secondaryDarkStyles}
    ${sizeDefaultStyles}
  `,
  secondary_dark_small: css`
    ${secondaryDarkStyles}
    ${sizeSmallStyles}
  `,
  secondary_dark_tiny: css`
    ${secondaryDarkStyles}
    ${sizeTinyStyles}
  `,
  tertiary: css`
    ${tertiaryStyles}
    ${animatedUnderlineStyles}
    ${tw`pb-[15px] pt-[11px]`}
    ${tw`px-0`}
    ${tw`py-[11px]`}
    ${tw`hover:after:scale-x-100`}

    &:after {
      ${tw`transition-all duration-150`}
      ${tw`absolute`}
      ${tw`origin-left scale-x-0`}
      ${tw`left-0 bottom-0`}
      ${tw`[content: '']`}
      ${tw`w-full h-[4px]`}
      ${tw`bg-secondary-s1`}
    }
    ${tw` disabled:text-ui-colours-ui3 disabled:hover:cursor-not-allowed disabled:hover:after:scale-x-0`}
  `,
  tertiary_no_underline: css`
    ${tertiaryStyles}
  `,
  tertiary_dark: css`
    ${tertiaryStyles}
    ${animatedUnderlineStyles}
    ${tw`pb-[15px] pt-[11px]`}
    ${tw`px-0`}
    ${tw`py-[11px]`}
    ${tw`hover:after:scale-x-100 hover:after:bg-ui-colours-ui5`}
    ${tw`text-primary-p1 hover:text-ui-colours-ui5`}
    ${tw`disabled:text-ui-colours-ui6 disabled:hover:cursor-not-allowed`}

    &:after {
      ${tw`transition-all duration-150`}
      ${tw`absolute`}
      ${tw`origin-left scale-x-0`}
      ${tw`left-0 bottom-0`}
      ${tw`[content: '']`}
      ${tw`w-full h-[4px]`}
      ${tw`bg-primary-p1`}
    }
  `,
  tertiary_dark_no_underline: css`
    ${tertiaryStyles}
    ${tw`text-primary-p1 hover:text-ui-colours-ui5`}
    ${tw`disabled:text-ui-colours-ui6 disabled:hover:cursor-not-allowed`}
  `,
  nav_text_link: css`
    ${tertiaryStyles}
    ${animatedUnderlineStyles}
    ${tw`h-full`}
    ${tw`min-h-[51px]`}
    ${tw`items-center`}
    ${tw`text-primary-p1 hover:text-primary-p1`}
  `,
  tab_primary: css`
    ${tabPrimaryStyles}
    ${tw`py-[13px] px-[20px]`}
  `,
  tab_primary_small: css`
    ${tabPrimaryStyles}
    ${tw`py-[9px] px-[20px]`}
  `,
  toggle: css`
    ${toggleStyles}
    ${tw`py-[12px] px-2`}
  `,
  toggle_small: css`
    ${toggleStyles}
    ${tw`py-[9px] px-2`}
  `,
  platform_login: css`
    ${plateformLoginStyles}
    ${tw`p-2`}
  `,
  platform_login_small: css`
    ${plateformLoginStyles}
    ${tw`px-3 py-[12px]`}
  `,
};

export const StyledIcon = styled(Icon)`
  ${tw`inline`}
  ${tw`h-[18px] w-[18px]`}
  ${tw`ml-[10px]`}
`;

export const linkIconStyles = css`
  ${StyledIcon} {
    ${tw`h-[16px] w-[16px]`}
    ${tw`ml-0`}
  }
`;

export const StyledLink = styled('a', {
  shouldForwardProp: (propName) => isPropValid(propName) && !['variant'].includes(propName),
})<{
  variant?: LinkVariant;
}>`
  ${({ variant }) =>
    variant &&
    css`
      ${variants[variant]}
      ${linkIconStyles}
    `}
`;
